import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

/**
 * Badge — DESIGN.md
 *
 *   <Badge>새 상품</Badge>                — primary (amber)
 *   <Badge variant="success">배송완료</Badge>
 *   <Badge variant="danger">환불</Badge>
 *   <Badge variant="warning">대기중</Badge>
 *   <Badge variant="neutral">옵션</Badge>
 *   <Badge variant="info">팁</Badge>
 *   <Badge variant="outline">테두리만</Badge>
 */
const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-colors',
  {
    variants: {
      variant: {
        primary:
          'border-transparent bg-primary text-primary-foreground',
        secondary:
          'border-transparent bg-surface-muted text-text dark:bg-gray-800 dark:text-gray-100',
        neutral:
          'border-transparent bg-surface-muted text-text-muted dark:bg-gray-800 dark:text-gray-300',
        success:
          'border-transparent bg-success-soft text-success dark:bg-emerald-900/30 dark:text-emerald-300',
        danger:
          'border-transparent bg-danger-soft text-danger dark:bg-red-900/30 dark:text-red-300',
        warning:
          'border-transparent bg-primary-soft text-primary-hover dark:bg-amber-900/30 dark:text-amber-300',
        info:
          'border-transparent bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
        outline:
          'border-border text-text dark:border-gray-700 dark:text-gray-100',

        // Legacy aliases — preserved for existing call sites
        default:
          'border-transparent bg-primary text-primary-foreground',
        destructive:
          'border-transparent bg-danger text-white',
        tip:
          'border-transparent bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
        agree:
          'border-transparent bg-success-soft text-success dark:bg-emerald-900/30 dark:text-emerald-300',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
